import {
  Container,
  Divider,
  ListItem,
  ListItemText,
  Paper,
  Stack,
} from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { PluginsForm } from '../plugins-form'
import { useTranslation } from 'react-i18next'
import { SectionHeader } from '@sitoo/mui-components'
import { useMutation, useQuery } from '@apollo/client'
import {
  GetExternalProductSearchDocument,
  PluginExternalProductSearch,
  UpdateExternalProductSearchDocument,
} from '../../../../generated/graphql'
import { TextFieldInput } from '../../../../inputs/text-field-input'
import { SwitchInput } from '../../../../inputs/switch-input'
import {
  AuthenticationDialog,
  AuthenticationDialogForm,
} from '../authentication/dialog'
import { AuthenticationSection } from '../authentication/section'
import { useState } from 'react'

type Form = PluginExternalProductSearch

const DEFAULT_VALUES: Form = {
  external_scan_to_cart: false,
  external_search_active: false,
  external_search_title: '',
  api_key: '',
  api_url: '',
}

export const ExternalSettingsPosProductSearchPage = () => {
  const { t } = useTranslation(['apps', 'shared'])
  const [isAuthOpen, setAuthOpen] = useState(false)

  const formContext = useForm<Form>({
    defaultValues: DEFAULT_VALUES,
  })

  const { control } = formContext

  const { data, loading: isLoading } = useQuery(
    GetExternalProductSearchDocument,
    {
      fetchPolicy: 'cache-and-network',
    },
  )

  const [updateSettings, { loading: isUpdating }] = useMutation(
    UpdateExternalProductSearchDocument,
  )

  const onSubmit = async (formData: Form) => {
    const { data } = await updateSettings({
      variables: {
        data: formData,
      },
    })

    return data?.updateExternalProductSearch
  }

  const onAuthSubmit = async (formData: AuthenticationDialogForm) => {
    const { login, password } = formData

    await updateSettings({
      variables: {
        data: {
          ...(data?.externalProductSearch || DEFAULT_VALUES),
          api_url: login,
          api_key: password,
        },
      },
    })
  }

  const isAuthConfigured = Boolean(
    data?.externalProductSearch?.api_url && data.externalProductSearch?.api_key,
  )

  return (
    <Container>
      <FormProvider {...formContext}>
        <PluginsForm
          serverData={data?.externalProductSearch}
          onSubmit={onSubmit}
          submitLabel={t('shared:action.save')}
          isLoading={isLoading || isUpdating}
        >
          <Paper sx={{ mb: 2 }}>
            <SectionHeader variant="transparent">
              {t('apps:pos_product_search.settings')}
            </SectionHeader>

            <ListItem
              component="div"
              secondaryAction={
                <SwitchInput
                  name="external_search_active"
                  type="checkbox"
                  control={control}
                  aria-label={t(
                    'apps:pos_product_search.external_search_active_label',
                  )}
                />
              }
            >
              <ListItemText
                primary={t(
                  'apps:pos_product_search.external_search_active_label',
                )}
                secondary={t(
                  'apps:pos_product_search.external_search_active_helper',
                )}
              />
            </ListItem>

            <Stack gap={2} sx={{ pt: 1, p: 2 }}>
              <TextFieldInput
                name="external_search_title"
                label={t('apps:pos_product_search.external_search_title_label')}
                helperText={t(
                  'apps:pos_product_search.external_search_title_helper',
                )}
                control={control}
                rules={{
                  maxLength: {
                    value: 16,
                    message: t('shared:validation.max_length', {
                      amount: 16,
                      field: t(
                        'apps:pos_product_search.external_search_title_label',
                      ),
                    }),
                  },
                }}
                transform={{
                  input: (value) => (value ? String(value) : ''),
                  output: (event) =>
                    event.currentTarget.value
                      ? String(event.currentTarget.value)
                      : null,
                }}
              />
            </Stack>
            <Divider />
            <ListItem
              secondaryAction={
                <SwitchInput
                  name="external_scan_to_cart"
                  type="checkbox"
                  control={control}
                  aria-label={t(
                    'apps:pos_product_search.external_scan_to_cart_label',
                  )}
                />
              }
            >
              <ListItemText
                primary={t(
                  'apps:pos_product_search.external_scan_to_cart_label',
                )}
                secondary={t(
                  'apps:pos_product_search.external_scan_to_cart_helper',
                )}
              />
            </ListItem>
          </Paper>
          <AuthenticationSection
            isConfigured={isAuthConfigured}
            onClick={() => setAuthOpen(true)}
            disabled={formContext.formState.isDirty}
          />
        </PluginsForm>
        <AuthenticationDialog
          open={isAuthOpen}
          onClose={() => setAuthOpen(false)}
          onSubmit={onAuthSubmit}
          defaultValues={{
            login: data?.externalProductSearch.api_url,
          }}
          slotProps={{
            loginInput: {
              label: t('apps:vipps_mobilepay.url_label'),
              helperText: t('apps:pos_product_search.api_url_helper'),
              rules: {
                startsWithHttps: (value) => {
                  if (value && !value.startsWith('https://'))
                    return t('apps:pos_product_search.url_https_error')
                },
                endsWithSlash: (value) => {
                  if (value && !value.endsWith('/'))
                    return t('apps:pos_product_search.url_trailing_slash_error')
                },
              },
            },
            passwordInput: {
              label: t('apps:pos_product_search.api_key_label'),
              helperText: t('apps:pos_product_search.api_key_helper'),
            },
          }}
        />
      </FormProvider>
    </Container>
  )
}
